"use client"

import * as React from "react"
import { Dialog as SheetPrimitive } from "@base-ui/react/dialog"

import { cn } from "@/lib/utils"

/**
 * A native bottom sheet — what a phone uses where a website uses a modal.
 *
 * Built on the same base-ui Dialog as `sheet.tsx`, so focus trapping, Escape,
 * tap-outside and scroll locking are the library's job. What this adds is the
 * part that makes it feel like a phone and not a web page (Apple, *Designing
 * Fluid Interfaces*):
 *
 * - it comes up from the bottom edge and goes back the same way (spatial
 *   consistency — in one way, out the same way);
 * - the handle tracks the finger 1:1 from where it was grabbed;
 * - a quarter of its height OR a flick closes it — a quick flick should be
 *   enough, nobody should have to drag a sheet all the way down;
 * - dragging UP resists instead of hitting a wall (rubber-band);
 * - the scrim fades with the drag, so the page underneath "comes back" as the
 *   sheet goes;
 * - no close ✕ on the surface. Swipe down, tap outside, Escape, or the
 *   hardware back gesture. A visually hidden close button stays for screen
 *   readers.
 *
 * Drag starts ONLY on elements marked `data-sheet-handle` (the built-in grab
 * bar, or a header the content marks). Dragging from scrollable content would
 * fight the browser's own scrolling and pull-to-refresh; the header is where
 * people reach for a sheet anyway.
 *
 * No animation library: the drag writes `translate` straight onto the element
 * and the settle is a CSS transition on the iOS drawer curve — off the main
 * thread, and nothing new in the POS bundle, which is the cashier's landing
 * route.
 */

function BottomSheet(props: SheetPrimitive.Root.Props) {
  return <SheetPrimitive.Root data-slot="bottom-sheet" {...props} />
}

function BottomSheetTrigger(props: SheetPrimitive.Trigger.Props) {
  return <SheetPrimitive.Trigger data-slot="bottom-sheet-trigger" {...props} />
}

/** Dragged past this share of its own height, the sheet closes on release. */
const DISMISS_FRACTION = 0.25
/** px/ms. Faster than this downward at release closes it whatever the distance. */
const DISMISS_VELOCITY = 0.4
/** px of movement before a press becomes a drag (hysteresis — a tap stays a tap). */
const DRAG_SLOP = 6
/** A velocity sample older than this means the finger stopped before lifting. */
const VELOCITY_STALE_MS = 80

/** Past the boundary, follow less the further it goes — never a hard stop. */
function rubberband(overshoot: number, dimension: number, constant = 0.55) {
  return (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot))
}

type DragState = {
  id: number
  startY: number
  lastY: number
  lastT: number
  vy: number
  active: boolean
}

type BottomSheetContentProps = SheetPrimitive.Popup.Props & {
  /** Read by screen readers; the sheet shows no title bar of its own. */
  title: string
  /**
   * `strip`   — the grab bar sits in its own row above the content.
   * `overlay` — the grab bar floats over the content's own header, for a
   *             sheet whose coloured header IS its top edge (the POS cart).
   *             Mark that header `data-sheet-handle` so it can be grabbed too.
   */
  handle?: "strip" | "overlay"
}

function BottomSheetContent({
  className,
  children,
  title,
  handle = "strip",
  ...props
}: BottomSheetContentProps) {
  const popupRef = React.useRef<HTMLDivElement>(null)
  const backdropRef = React.useRef<HTMLDivElement>(null)
  const closeRef = React.useRef<HTMLButtonElement>(null)
  const drag = React.useRef<DragState | null>(null)

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // One finger at a time — a second touch mid-drag must not make it jump.
    if (drag.current || e.button !== 0) return
    const target = e.target as HTMLElement
    if (!target.closest("[data-sheet-handle]")) return
    if (target.closest("button, a, input, select, textarea, [data-no-drag]")) return
    drag.current = {
      id: e.pointerId,
      startY: e.clientY,
      lastY: e.clientY,
      lastT: e.timeStamp,
      vy: 0,
      active: false,
    }
  }

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current
    const el = popupRef.current
    if (!d || !el || e.pointerId !== d.id) return
    const dy = e.clientY - d.startY

    if (!d.active) {
      if (Math.abs(dy) < DRAG_SLOP) return
      d.active = true
      // Keep receiving moves even when the finger leaves the handle.
      el.setPointerCapture(e.pointerId)
      el.style.transition = "none"
      if (backdropRef.current) backdropRef.current.style.transition = "none"
    }

    const h = el.offsetHeight || 1
    const y = dy >= 0 ? dy : rubberband(dy, h)
    // `translate`, not `transform`: Tailwind v4's translate-y-* utilities
    // write the `translate` property, and the two would ADD together.
    el.style.translate = `0 ${y}px`
    if (backdropRef.current) {
      backdropRef.current.style.opacity = String(Math.max(0, 1 - Math.max(0, y) / h))
    }

    const dt = e.timeStamp - d.lastT
    if (dt > 0) d.vy = (e.clientY - d.lastY) / dt
    d.lastY = e.clientY
    d.lastT = e.timeStamp
  }

  const finish = (e: React.PointerEvent<HTMLDivElement>, cancelled: boolean) => {
    const d = drag.current
    const el = popupRef.current
    if (!d || e.pointerId !== d.id) return
    drag.current = null
    if (!el || !d.active) return

    const dy = e.clientY - d.startY
    const h = el.offsetHeight || 1
    const vy = e.timeStamp - d.lastT > VELOCITY_STALE_MS ? 0 : d.vy
    const shouldClose = !cancelled && dy > 0 && (dy > h * DISMISS_FRACTION || vy > DISMISS_VELOCITY)

    // Hand the motion back to the CSS transition — from wherever the finger
    // left it, which is what makes the release feel continuous.
    el.style.transition = ""
    const backdrop = backdropRef.current

    if (shouldClose) {
      el.style.translate = "0 100%"
      if (backdrop) {
        backdrop.style.transition = "opacity 260ms ease-out"
        backdrop.style.opacity = "0"
      }
      closeRef.current?.click()
    } else {
      el.style.translate = ""
      if (backdrop) {
        backdrop.style.transition = ""
        backdrop.style.opacity = ""
      }
    }
  }

  return (
    <SheetPrimitive.Portal>
      <SheetPrimitive.Backdrop
        ref={backdropRef}
        data-slot="bottom-sheet-backdrop"
        className="fixed inset-0 z-50 bg-black/45 transition-opacity duration-[420ms] ease-[var(--ease-drawer)] data-starting-style:opacity-0 data-ending-style:opacity-0 data-ending-style:duration-[260ms]"
      />
      <SheetPrimitive.Popup
        ref={popupRef}
        data-slot="bottom-sheet"
        // Never land the focus in an input: on a phone that opens the keyboard
        // over half the sheet the moment it arrives.
        initialFocus={popupRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={(e) => finish(e, false)}
        onPointerCancel={(e) => finish(e, true)}
        className={cn(
          // Above the on-screen keyboard, never under it (lib/keyboard-inset.ts):
          // the stocktake's typed count and PIN live in sheets.
          "fixed inset-x-0 bottom-[var(--kb-inset)] z-50 flex max-h-[calc(var(--vv-avail)*0.94)] flex-col overflow-hidden rounded-t-[28px] bg-background text-foreground outline-none",
          "shadow-[0_-12px_48px_-16px_rgb(0_0_0/0.45)] pb-[env(safe-area-inset-bottom)]",
          "transition-[translate,opacity] duration-[420ms] ease-[var(--ease-drawer)]",
          "data-starting-style:translate-y-full data-ending-style:translate-y-full data-ending-style:duration-[260ms]",
          // Reduced motion: a short cross-fade instead of a slide.
          "motion-reduce:duration-200 motion-reduce:data-starting-style:translate-y-0 motion-reduce:data-starting-style:opacity-0 motion-reduce:data-ending-style:translate-y-0 motion-reduce:data-ending-style:opacity-0",
          className
        )}
        {...props}
      >
        <SheetPrimitive.Title className="sr-only">{title}</SheetPrimitive.Title>

        {handle === "strip" ? (
          <div
            data-sheet-handle
            className="flex shrink-0 cursor-grab touch-none justify-center pt-2.5 pb-2 active:cursor-grabbing"
          >
            <span className="h-[5px] w-10 rounded-full bg-foreground/20" />
          </div>
        ) : (
          <div
            data-sheet-handle
            aria-hidden
            className="absolute inset-x-0 top-0 z-20 flex h-4 cursor-grab touch-none justify-center pt-2 active:cursor-grabbing"
          >
            {/* On the content's own coloured header (the POS cart gradient),
                so white in both themes — a themed surface colour here turned
                into a dark smudge in dark mode (audit, 2026-09-11). */}
            <span className="h-[5px] w-10 rounded-full bg-white/70" />
          </div>
        )}

        {children}

        <SheetPrimitive.Close ref={closeRef} className="sr-only">
          დახურვა
        </SheetPrimitive.Close>
      </SheetPrimitive.Popup>
    </SheetPrimitive.Portal>
  )
}

export { BottomSheet, BottomSheetTrigger, BottomSheetContent }
